import React, { useRef, useCallback, useEffect, useState } from 'react';
import type { SocialCanvasElement, SocialCanvasElementType } from '../../../services/socialPosting';
import {
  MIN_ELEMENT_SIZE,
  HANDLE_SIZE,
  CANVAS_PADDING,
  SNAP_THRESHOLD,
} from '../../canvas-shared/canvasConstants';
import { EmptyCanvasPlaceholder } from '../../canvas-shared/EmptyCanvasPlaceholder';

type HandleDirection = 'nw' | 'n' | 'ne' | 'w' | 'e' | 'sw' | 's' | 'se';

const HANDLES: {
  dir: HandleDirection;
  cursor: string;
  getPos: (w: number, h: number, half: number) => { x: number; y: number };
}[] = [
  { dir: 'nw', cursor: 'nwse-resize', getPos: (_w, _h, half) => ({ x: -half, y: -half }) },
  { dir: 'n', cursor: 'ns-resize', getPos: (w, _h, half) => ({ x: w / 2 - half, y: -half }) },
  { dir: 'ne', cursor: 'nesw-resize', getPos: (w, _h, half) => ({ x: w - half, y: -half }) },
  { dir: 'w', cursor: 'ew-resize', getPos: (_w, h, half) => ({ x: -half, y: h / 2 - half }) },
  { dir: 'e', cursor: 'ew-resize', getPos: (w, h, half) => ({ x: w - half, y: h / 2 - half }) },
  { dir: 'sw', cursor: 'nesw-resize', getPos: (_w, h, half) => ({ x: -half, y: h - half }) },
  { dir: 's', cursor: 'ns-resize', getPos: (w, h, half) => ({ x: w / 2 - half, y: h - half }) },
  { dir: 'se', cursor: 'nwse-resize', getPos: (w, h, half) => ({ x: w - half, y: h - half }) },
];

interface Interaction {
  mode: 'drag' | 'resize' | 'rotate';
  elementIndex: number;
  startMouseX: number;
  startMouseY: number;
  startElX: number;
  startElY: number;
  startElW: number;
  startElH: number;
  handleDir?: HandleDirection;
  startAngle?: number;
  startRotation?: number;
}

interface SnapGuide {
  orientation: 'horizontal' | 'vertical';
  position: number;
}

// ── Helpers ──────────────────────────────────────────────────

function truncateText(text: string, maxChars: number): string {
  if (!maxChars || maxChars <= 0) return text;
  if (text.length <= maxChars) return text;
  return text.slice(0, maxChars) + '\u2026';
}

function buildFilterString(filter?: { grayscale?: number; sepia?: number; brightness?: number; contrast?: number }): string {
  if (!filter) return 'none';
  const parts: string[] = [];
  if (filter.grayscale) parts.push(`grayscale(${filter.grayscale}%)`);
  if (filter.sepia) parts.push(`sepia(${filter.sepia}%)`);
  if (filter.brightness !== undefined && filter.brightness !== 100) parts.push(`brightness(${filter.brightness}%)`);
  if (filter.contrast !== undefined && filter.contrast !== 100) parts.push(`contrast(${filter.contrast}%)`);
  return parts.length > 0 ? parts.join(' ') : 'none';
}

function buildShadowString(shadow?: { x: number; y: number; blur: number; color: string }): string {
  if (!shadow) return 'none';
  return `${shadow.x}px ${shadow.y}px ${shadow.blur}px ${shadow.color}`;
}

// ── Element Renderer ─────────────────────────────────────────

function renderElementContent(
  el: SocialCanvasElement,
  resolveValues: boolean,
  sampleData?: Record<string, string>,
) {
  const resolveTokens = (str: string) => {
    if (!resolveValues || !sampleData) return str;
    return str.replace(/\{\{([^}]+)\}\}/g, (_, key) => sampleData[key] ?? `{{${key}}}`);
  };

  switch (el.type) {
    case 'text': {
      let resolved = resolveTokens(el.content);
      if (el.maxChars && el.maxChars > 0) {
        resolved = truncateText(resolved, el.maxChars);
      }
      return (
        <div
          style={{
            fontSize: el.fontSize,
            fontWeight: el.fontWeight,
            fontFamily: el.fontFamily,
            color: el.color,
            textAlign: el.textAlign,
            overflow: 'hidden',
            width: '100%',
            height: '100%',
            wordBreak: 'break-word',
            lineHeight: el.lineHeight ?? 1.3,
            letterSpacing: el.letterSpacing ? `${el.letterSpacing}px` : undefined,
            textTransform: el.textTransform || 'none',
            textShadow: el.textShadow || 'none',
          }}
        >
          <span style={{ whiteSpace: 'pre-wrap' }}>{resolved}</span>
        </div>
      );
    }
    case 'image': {
      const rawSrc = resolveTokens(el.src);
      const isToken = !rawSrc || rawSrc.startsWith('{{');
      const src = (!isToken && el.removeBg && el.bgRemovedSrc) || rawSrc;
      const filterStr = buildFilterString(el.filter);
      return isToken ? (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.04)',
          border: '1px dashed rgba(0,0,0,0.2)', borderRadius: 4, fontSize: 12, color: 'rgba(0,0,0,0.4)',
        }}>
          {src || 'No image'}
        </div>
      ) : (
        <img
          src={src}
          alt=""
          draggable={false}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
          style={{
            width: '100%', height: '100%', objectFit: el.objectFit as any,
            borderRadius: el.borderRadius, display: 'block',
            filter: filterStr,
          }}
        />
      );
    }
    case 'rect':
      return (
        <div style={{
          width: '100%', height: '100%', backgroundColor: el.fill,
          borderRadius: el.borderRadius, border: el.border || 'none',
        }} />
      );
    case 'circle':
      return (
        <div style={{
          width: '100%', height: '100%', backgroundColor: el.fill, borderRadius: '50%',
        }} />
      );
    default:
      return null;
  }
}

// ── Component ────────────────────────────────────────────────

interface SocialCanvasWorkspaceProps {
  elements: SocialCanvasElement[];
  selectedIndex: number | null;
  canvasWidth: number;
  canvasHeight: number;
  zoom: number;
  isPreview: boolean;
  sampleData?: Record<string, string>;
  showGrid?: boolean;
  onSelectElement: (index: number | null) => void;
  onUpdateElement: (index: number, updates: Partial<SocialCanvasElement>) => void;
  onAddElement?: (type: SocialCanvasElementType) => void;
}

export function SocialCanvasWorkspace({
  elements,
  selectedIndex,
  canvasWidth,
  canvasHeight,
  zoom,
  isPreview,
  sampleData,
  showGrid,
  onSelectElement,
  onUpdateElement,
  onAddElement,
}: SocialCanvasWorkspaceProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [interaction, setInteraction] = useState<Interaction | null>(null);
  const [snapGuides, setSnapGuides] = useState<SnapGuide[]>([]);

  const scaledW = canvasWidth * zoom;
  const scaledH = canvasHeight * zoom;

  const toCanvasCoords = useCallback(
    (clientX: number, clientY: number) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };
      return { x: (clientX - rect.left) / zoom, y: (clientY - rect.top) / zoom };
    },
    [zoom],
  );

  // Snap guides calculation
  const computeSnap = useCallback(
    (elIndex: number, newX: number, newY: number, w: number, h: number) => {
      const guides: SnapGuide[] = [];
      let snappedX = newX;
      let snappedY = newY;

      // Canvas center
      const cx = canvasWidth / 2;
      const cy = canvasHeight / 2;
      const elCx = newX + w / 2;
      const elCy = newY + h / 2;

      if (Math.abs(elCx - cx) < SNAP_THRESHOLD) {
        snappedX = cx - w / 2;
        guides.push({ orientation: 'vertical', position: cx });
      }
      if (Math.abs(elCy - cy) < SNAP_THRESHOLD) {
        snappedY = cy - h / 2;
        guides.push({ orientation: 'horizontal', position: cy });
      }

      // Canvas edges
      if (Math.abs(newX) < SNAP_THRESHOLD) { snappedX = 0; guides.push({ orientation: 'vertical', position: 0 }); }
      if (Math.abs(newY) < SNAP_THRESHOLD) { snappedY = 0; guides.push({ orientation: 'horizontal', position: 0 }); }
      if (Math.abs(newX + w - canvasWidth) < SNAP_THRESHOLD) { snappedX = canvasWidth - w; guides.push({ orientation: 'vertical', position: canvasWidth }); }
      if (Math.abs(newY + h - canvasHeight) < SNAP_THRESHOLD) { snappedY = canvasHeight - h; guides.push({ orientation: 'horizontal', position: canvasHeight }); }

      return { snappedX, snappedY, guides };
    },
    [canvasWidth, canvasHeight],
  );

  // Drag / resize interaction
  useEffect(() => {
    if (!interaction) return;

    const handleMouseMove = (e: MouseEvent) => {
      const { x: mouseX, y: mouseY } = toCanvasCoords(e.clientX, e.clientY);
      const dx = mouseX - interaction.startMouseX;
      const dy = mouseY - interaction.startMouseY;

      if (interaction.mode === 'rotate') {
        const el = elements[interaction.elementIndex];
        const cx = el.x + el.width / 2;
        const cy = el.y + el.height / 2;
        const currentAngle = Math.atan2(mouseY - cy, mouseX - cx);
        const deltaAngle = currentAngle - interaction.startAngle!;
        const degrees = (interaction.startRotation! + deltaAngle * (180 / Math.PI)) % 360;
        onUpdateElement(interaction.elementIndex, {
          rotation: Math.round(degrees < 0 ? degrees + 360 : degrees),
        });
        return;
      }

      if (interaction.mode === 'drag') {
        const rawX = Math.round(interaction.startElX + dx);
        const rawY = Math.round(interaction.startElY + dy);
        const { snappedX, snappedY, guides } = computeSnap(
          interaction.elementIndex, rawX, rawY,
          interaction.startElW, interaction.startElH,
        );
        setSnapGuides(guides);
        onUpdateElement(interaction.elementIndex, { x: snappedX, y: snappedY });
        return;
      }

      // Resize
      const dir = interaction.handleDir!;
      let newX = interaction.startElX;
      let newY = interaction.startElY;
      let newW = interaction.startElW;
      let newH = interaction.startElH;

      if (dir.includes('e')) newW = Math.max(MIN_ELEMENT_SIZE, interaction.startElW + dx);
      if (dir.includes('w')) { newW = Math.max(MIN_ELEMENT_SIZE, interaction.startElW - dx); newX = interaction.startElX + (interaction.startElW - newW); }
      if (dir.includes('s')) newH = Math.max(MIN_ELEMENT_SIZE, interaction.startElH + dy);
      if (dir.includes('n')) { newH = Math.max(MIN_ELEMENT_SIZE, interaction.startElH - dy); newY = interaction.startElY + (interaction.startElH - newH); }

      const el = elements[interaction.elementIndex];
      if (el.type === 'circle') { const size = Math.max(newW, newH); newW = size; newH = size; }

      onUpdateElement(interaction.elementIndex, {
        x: Math.round(newX), y: Math.round(newY), width: Math.round(newW), height: Math.round(newH),
      });
    };

    const handleMouseUp = () => {
      setInteraction(null);
      setSnapGuides([]);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [interaction, toCanvasCoords, onUpdateElement, elements, computeSnap]);

  const handleBackgroundMouseDown = useCallback(() => {
    onSelectElement(null);
  }, [onSelectElement]);

  const handleElementMouseDown = useCallback(
    (e: React.MouseEvent, index: number) => {
      e.stopPropagation();
      onSelectElement(index);
      const el = elements[index];
      if (el.locked) return;
      const { x, y } = toCanvasCoords(e.clientX, e.clientY);
      setInteraction({
        mode: 'drag', elementIndex: index,
        startMouseX: x, startMouseY: y,
        startElX: el.x, startElY: el.y, startElW: el.width, startElH: el.height,
      });
    },
    [elements, onSelectElement, toCanvasCoords],
  );

  const handleHandleMouseDown = useCallback(
    (e: React.MouseEvent, index: number, dir: HandleDirection) => {
      e.stopPropagation();
      const el = elements[index];
      if (el.locked) return;
      const { x, y } = toCanvasCoords(e.clientX, e.clientY);
      setInteraction({
        mode: 'resize', elementIndex: index, handleDir: dir,
        startMouseX: x, startMouseY: y,
        startElX: el.x, startElY: el.y, startElW: el.width, startElH: el.height,
      });
    },
    [elements, toCanvasCoords],
  );

  const handleRotateMouseDown = useCallback(
    (e: React.MouseEvent, index: number) => {
      e.stopPropagation();
      const el = elements[index];
      if (el.locked) return;
      const { x: mouseX, y: mouseY } = toCanvasCoords(e.clientX, e.clientY);
      const cx = el.x + el.width / 2;
      const cy = el.y + el.height / 2;
      setInteraction({
        mode: 'rotate', elementIndex: index,
        startMouseX: mouseX, startMouseY: mouseY,
        startElX: el.x, startElY: el.y, startElW: el.width, startElH: el.height,
        startAngle: Math.atan2(mouseY - cy, mouseX - cx),
        startRotation: el.rotation ?? 0,
      });
    },
    [elements, toCanvasCoords],
  );

  const handleVisualSize = HANDLE_SIZE / zoom;
  const handleHalf = handleVisualSize / 2;

  return (
    <div
      className="flex-1 min-w-0 overflow-auto"
      style={{ backgroundColor: '#e8e8e8' }}
      onMouseDown={handleBackgroundMouseDown}
    >
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minWidth: '100%', minHeight: '100%',
        width: scaledW + CANVAS_PADDING * 2, height: scaledH + CANVAS_PADDING * 2,
      }}>
        <div style={{
          width: scaledW, height: scaledH, position: 'relative', overflow: 'hidden',
          borderRadius: 2, boxShadow: '0 2px 16px rgba(0,0,0,0.10), 0 0 0 1px rgba(0,0,0,0.06)', flexShrink: 0,
        }}>
          <div
            ref={canvasRef}
            style={{
              position: 'absolute', top: 0, left: 0, width: canvasWidth, height: canvasHeight,
              backgroundColor: '#ffffff', transform: `scale(${zoom})`, transformOrigin: '0 0',
              ...(showGrid ? {
                backgroundImage: `
                  linear-gradient(rgba(0,0,0,0.15) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(0,0,0,0.15) 1px, transparent 1px)
                `,
                backgroundSize: '20px 20px',
              } : {}),
            }}
          >
            {/* Empty canvas placeholder */}
            {elements.length === 0 && onAddElement && (
              <EmptyCanvasPlaceholder onAddElement={onAddElement as (type: 'text' | 'image' | 'rect') => void} />
            )}

            {/* Snap guides */}
            {snapGuides.map((guide, i) => (
              <div
                key={i}
                data-export-ignore="true"
                style={{
                  position: 'absolute',
                  ...(guide.orientation === 'vertical'
                    ? { left: guide.position, top: 0, width: 1 / zoom, height: canvasHeight, backgroundColor: 'hsl(var(--primary))' }
                    : { left: 0, top: guide.position, width: canvasWidth, height: 1 / zoom, backgroundColor: 'hsl(var(--primary))' }
                  ),
                  opacity: 0.5,
                  pointerEvents: 'none',
                  zIndex: 100,
                }}
              />
            ))}

            {elements.map((el, idx) => {
              const isSelected = selectedIndex === idx;
              const isVisible = el.visible !== false;
              if (!isVisible) return null;

              return (
                <div
                  key={el.id || idx}
                  onMouseDown={(e) => handleElementMouseDown(e, idx)}
                  style={{
                    position: 'absolute', left: el.x, top: el.y, width: el.width, height: el.height,
                    transform: `rotate(${el.rotation ?? 0}deg)`, transformOrigin: 'center center',
                    opacity: el.opacity ?? 1,
                    boxShadow: buildShadowString(el.shadow),
                    outline: isSelected ? `${2 / zoom}px solid hsl(var(--primary))` : 'none',
                    outlineOffset: `${-1 / zoom}px`,
                    cursor: el.locked ? 'default' : interaction?.mode === 'drag' && interaction.elementIndex === idx ? 'grabbing' : 'grab',
                    userSelect: 'none', overflow: 'hidden',
                  }}
                >
                  {renderElementContent(el, isPreview, sampleData)}

                  {/* Resize handles */}
                  {isSelected && !el.locked && HANDLES.map((handle) => {
                    const pos = handle.getPos(el.width, el.height, handleHalf);
                    return (
                      <div
                        key={handle.dir}
                        data-export-ignore="true"
                        onMouseDown={(e) => handleHandleMouseDown(e, idx, handle.dir)}
                        style={{
                          position: 'absolute', left: pos.x, top: pos.y,
                          width: handleVisualSize, height: handleVisualSize,
                          backgroundColor: 'hsl(var(--primary))',
                          border: `${1.5 / zoom}px solid hsl(var(--background))`,
                          borderRadius: 2 / zoom, cursor: handle.cursor, zIndex: 10,
                        }}
                      />
                    );
                  })}

                  {/* Rotation zones */}
                  {isSelected && !el.locked && (['nw', 'ne', 'sw', 'se'] as const).map((corner) => {
                    const rotZoneSize = 12 / zoom;
                    const offset = rotZoneSize + handleHalf;
                    const positions: Record<string, { left: number; top: number }> = {
                      nw: { left: -offset, top: -offset },
                      ne: { left: el.width + handleHalf, top: -offset },
                      sw: { left: -offset, top: el.height + handleHalf },
                      se: { left: el.width + handleHalf, top: el.height + handleHalf },
                    };
                    return (
                      <div
                        key={`rot-${corner}`}
                        data-export-ignore="true"
                        onMouseDown={(e) => handleRotateMouseDown(e, idx)}
                        style={{
                          position: 'absolute', left: positions[corner].left, top: positions[corner].top,
                          width: rotZoneSize, height: rotZoneSize, cursor: 'grab', zIndex: 9,
                        }}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
